import { SharedCommonUtility } from './common.utility';
import { IParsedStandard } from '../interfaces/standard-parsing.interface';
import { ParsedStandards } from '../constants/standard-parsing';
import { IAuditStandard } from '../interfaces/audit-standard.interface';
import { $auditStandard, mapAuditDbStandardToAuditStandard } from '../constants/audit-standard';
import { IPopulatedManualFindingStandard } from '../interfaces/manual-audit-finding.interface';
import { standardNameAndLevel } from '../constants/scanning';
import {
  EVALUATION_STANDARDS,
  EVALUATION_STANDARDS_BY_CONFORMANCE_LEVEL,
  EvaluationStandards,
} from '../constants/manual-evaluation';

export class SharedAuditStandardsUtility {
  public static filterStandards<AuditStandard extends IAuditStandard | IPopulatedManualFindingStandard>(
    auditStandards: AuditStandard[],
    nameVersionLevel: string,
  ): AuditStandard[] {
    const { standard, version, levels }: IParsedStandard = ParsedStandards[nameVersionLevel] ?? { standard: nameVersionLevel };

    const compareStandard = (value: AuditStandard): boolean =>
      mapAuditDbStandardToAuditStandard[value[$auditStandard.standard]] === standard;
    const compareVersion = (value: AuditStandard): boolean => value[$auditStandard.version] === version;
    const compareLevel = (value: AuditStandard): boolean => levels.includes(value[$auditStandard.level]);

    if (SharedCommonUtility.isNullish(version) && SharedCommonUtility.isNullishOrEmpty(levels)) {
      return auditStandards.filter(compareStandard);
    }

    if (SharedCommonUtility.isNullish(version)) {
      return auditStandards.filter((value: AuditStandard): boolean => compareStandard(value) && compareLevel(value));
    }

    if (SharedCommonUtility.isNullish(levels)) {
      return auditStandards.filter((value: AuditStandard): boolean => compareStandard(value) && compareVersion(value));
    }

    return auditStandards.filter(
      (value: AuditStandard): boolean => compareStandard(value) && compareLevel(value) && compareVersion(value),
    );
  }

  public static conformanceLevelToEvaluationStandard(conformanceLevel: standardNameAndLevel): EvaluationStandards {
    if (!EVALUATION_STANDARDS.includes(conformanceLevel as unknown as EvaluationStandards)) {
      throw new Error('Cannot convert conformance level to evaluation standard, conformance level value is likely invalid');
    }
    return EvaluationStandards[conformanceLevel];
  }

  public static conformanceLevelToEvaluationStandardIncludingWcagSubstandards(
    conformanceLevel: standardNameAndLevel,
  ): EvaluationStandards[] {
    if (!EVALUATION_STANDARDS.includes(conformanceLevel as unknown as EvaluationStandards)) {
      throw new Error('Cannot convert conformance level to evaluation standard, conformance level value is likely invalid');
    }
    if (EVALUATION_STANDARDS_BY_CONFORMANCE_LEVEL[conformanceLevel]) {
      return EVALUATION_STANDARDS_BY_CONFORMANCE_LEVEL[conformanceLevel];
    }
    return [EvaluationStandards[conformanceLevel]];
  }

  public static inflateWcagStandard(standard: EvaluationStandards): EvaluationStandards[] {
    const standardStr: string = standard.toString();
    if (!standardStr.startsWith('WCAG')) {
      return [standard];
    }
    const prefix: string = standardStr.slice(0, standardStr.lastIndexOf('_'));
    const suffix: string = standardStr.slice(prefix.length);
    const allStandards: EvaluationStandards[] = Object.values(EvaluationStandards) as EvaluationStandards[];
    return allStandards.filter(
      (s: EvaluationStandards) => s.toString().startsWith(prefix) && s.toString().slice(prefix.length).length <= suffix.length,
    );
  }

  public static deflateWcagStandards(standards: EvaluationStandards[]): EvaluationStandards {
    const wcagOnly: EvaluationStandards[] = standards?.filter((s: EvaluationStandards) => s.toString().startsWith('WCAG'));
    if (SharedCommonUtility.isNullishOrEmpty(wcagOnly)) {
      return undefined;
    }
    const prefix: string = wcagOnly[0].toString().slice(0, wcagOnly[0].toString().lastIndexOf('_'));
    if (wcagOnly.some((s: EvaluationStandards) => !s.toString().startsWith(prefix))) {
      throw new Error(`Not all standards share the same prefix ${wcagOnly}`);
    }
    wcagOnly.sort((s1: EvaluationStandards, s2: EvaluationStandards) => s2.toString().length - s1.toString().length);
    return wcagOnly[0];
  }

  /**
   * Formats a given EvaluationStandards enum value into a human-readable string.
   * For Section 508 and EN 301 549, the version is omitted.
   *
   * Examples:
   * WCAG_v21_AA -> "WCAG 2.1 AA"
   * SECTION_508 -> "Section 508"
   * EN_301_549 -> "EN 301 549"
   */
  public static formatStandardLabel(standard: EvaluationStandards): string {
    const parsed: IParsedStandard = ParsedStandards[standard];

    if (SharedCommonUtility.isNullish(parsed)) {
      return String(standard);
    }

    const standardLabelSections: string[] = [String(parsed.standard)];

    const shouldOmitVersion: boolean = standard === EvaluationStandards.section508 || standard === EvaluationStandards.en301549;

    if (!shouldOmitVersion && SharedCommonUtility.notNullishOrEmpty(parsed.version)) {
      standardLabelSections.push(String(parsed.version));
    }

    const level: string | undefined = parsed.levels?.[parsed.levels.length - 1];
    if (SharedCommonUtility.notNullishOrEmpty(level)) {
      standardLabelSections.push(level);
    }

    return standardLabelSections.join(' ');
  }
}
